import {
    ModalOverlay,
    ModalDialog,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    DateField,
    SelectableBoxField,
    LoadingDots,
    Message,
    Box
} from '@flos/react-ui';
import { BranchData, CarbucksReservation, CarbucksTimeslot } from '../../../types/services';
import { addDays } from '../../../utils/getCurrentDate';
import { useContext, useEffect, useState } from 'react';
import {
    CarbucksService,
    CreateReservationParams,
    GetTimeslotsParams,
    UpdateReservationParams
} from '../../../services/carBucks';
import { ConfigContext } from '../../../context/ConfigContext';
import { getYMDStringFromDateObject } from '../../../helpers/dates';
import LinkGoogleMaps from '../LinkGoogleMaps';
import LinkPhonenumber from '../LinkPhonenumber';
import useAppState from '../../../state/appState';
import { trackInfo } from '../../../track-by-submit/common/tracking';

export type CloseStatus = 'timeslotsApiFailed' | 'reservationApiFailed' | 'ok';

type CarbucksBookingProps = {
    isModalOpen: boolean;
    selectedWorkshop: BranchData | null;
    existingReservation: CarbucksReservation | null;

    onCloseClick: (status: CloseStatus) => void;
    onBookingReservation: (reservation: CarbucksReservation, workshop: BranchData | null) => void;
};

type TimeSlot = {
    label: string;
    value: string;
};

const CarbucksBooking = ({
    isModalOpen,
    selectedWorkshop,
    existingReservation,
    onCloseClick,
    onBookingReservation
}: CarbucksBookingProps) => {
    const appConfig = useContext(ConfigContext);
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);

    const today = new Date();
    const minCarbucksDate: Date = addDays(today, 5);
    const maxCarbucksDate: Date = addDays(today, 60);

    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
    const [isTimeslotsLoading, setIsTimeslotsLoading] = useState<boolean>(false);
    const [resetKey, setResetKey] = useState(0);

    const [allTimeslots, setAllTimeslots] = useState<CarbucksTimeslot[] | null>(null);
    const [availableTimeslotsOnDate, setAvailableTimeslotsOnDate] = useState<TimeSlot[]>([]);
    const [excludeDays, setExcludeDays] = useState<Date[]>([]);

    const [showReservationConfirm, setShowReservationConfirm] = useState<boolean>(false);
    const [isCreateReservationLoading, setIsCreateReservationLoading] = useState<boolean>(false);
    const [isReservationError, setIsReservationError] = useState<boolean>(false);

    // Fetches all available timeslots for the selected workshop for a certain date period
    useEffect(
        () => {
            const fetchTimeslots = async (startDate: Date, endDate: Date, carbucksGUID: string) => {
                setIsTimeslotsLoading(true);

                const params: GetTimeslotsParams = {
                    startDate: getYMDStringFromDateObject(startDate),
                    endDate: getYMDStringFromDateObject(endDate),
                    carbucksGUID: carbucksGUID
                };

                const timeslots: CarbucksTimeslot[] | null = await CarbucksService.getTimeslots(appConfig, params);

                if (!timeslots) {
                    formConfig && trackInfo(formConfig, [{ key: 'carbucks-timeslot-error', value: true }]);
                    resetBookingState();
                    onCloseClick('timeslotsApiFailed');
                    setIsTimeslotsLoading(false);
                    return;
                }

                // Find the earliest available timeslot date
                const availableDates = Array.from(
                    new Set(timeslots.map((slot) => slot.slotStartDatetime.split(' ')[0]))
                ).sort(); // 'YYYY-MM-DD' strings

                // Choose next available date or fallback
                const carbucksDefaultStartDate = availableDates.length
                    ? new Date(availableDates[0])
                    : addDays(today, 5);

                setAllTimeslots(timeslots);
                setIsTimeslotsLoading(false);
                setSelectedDate(carbucksDefaultStartDate);
            };

            // we only need to fetch the availaible timeslots once per selected workshop, since we fetch the availability
            // for a larger dateperiod in once go
            if (isModalOpen && selectedWorkshop && !isTimeslotsLoading) {
                fetchTimeslots(minCarbucksDate, maxCarbucksDate, selectedWorkshop.carbucksGUID);
            }
        },
        // only run this useEffect when the modal is opened and a workshop is selected/changed
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isModalOpen, selectedWorkshop]
    );

    // Sets up the exclude days for the datepicker (dates when no timeslots are available)
    useEffect(
        () => {
            // get all dates with available timeslots
            const availableDates = allTimeslots?.map((slot) => {
                const timeslotDate = slot.slotStartDatetime.split(' ')[0];
                return timeslotDate;
            });

            if (availableDates) {
                const calendardaysToExclude = [];

                // Iterate over the date period, and per day evaulate if that date has any available timeslots or not,
                // if not then add that date to the exclude array (such that those days can be grayed out in the calendar)
                for (let d = minCarbucksDate; d <= maxCarbucksDate; d = addDays(d, 1)) {
                    if (availableDates?.indexOf(getYMDStringFromDateObject(d)) === -1) {
                        calendardaysToExclude.push(d);
                    }
                }

                setExcludeDays(calendardaysToExclude);
            }
        },
        // only run this useEffect when the all the fetched timeslots changes (when modal is re-opened)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allTimeslots]
    );

    // Sets up the available timeslots for the selected date
    useEffect(
        () => {
            const timeslotsOnActiveDay = allTimeslots?.filter((slot) => {
                const timeslotDate = new Date(slot.slotStartDatetime.split(' ')[0]);
                return timeslotDate.toDateString() === selectedDate?.toDateString();
            });

            if (timeslotsOnActiveDay && timeslotsOnActiveDay.length > 0) {
                setAvailableTimeslotsOnDate(
                    timeslotsOnActiveDay.map((slot) => {
                        const timeStart = slot.slotStartDatetime.split(' ')[1].substring(0, 5);
                        const timeEnd = slot.slotEndDatetime.split(' ')[1].substring(0, 5);

                        return {
                            label: timeStart + ' - ' + timeEnd,
                            value: timeStart + ' - ' + timeEnd
                        };
                    })
                );
            } else {
                setAvailableTimeslotsOnDate([]);
            }
        },
        // only run this useEffect when the selectedDate changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedDate]
    );

    // Resets the booking state, such that the user can start from fresh again
    const resetBookingState = () => {
        setAvailableTimeslotsOnDate([]); // important that we reset the timeslots here, since user might re-open carbucks booking modal for a different workshop id
        setSelectedDate(null);
        setSelectedTimeSlot('');
        setShowReservationConfirm(false);
        setIsCreateReservationLoading(false);
        setIsReservationError(false);
        setSelectedDate(null);
        setResetKey((prev) => prev + 1);
    };

    const onDateChange = (date: Date | null) => {
        if (!date) return;
        setSelectedTimeSlot('');
        //setFormValue(elementId, format(date, 'dd-MM-yyyy'));
        setSelectedDate(date);
    };

    const onConfirmReservation = async () => {
        setIsCreateReservationLoading(true);

        let bookingReservation: CarbucksReservation | null | boolean = null;

        if (existingReservation) {
            const updateParams: UpdateReservationParams = {
                reservationGUID: existingReservation.reservationGUID,
                orderGUID: existingReservation.orderGUID,

                carbucksGUID: selectedWorkshop?.carbucksGUID || '',
                slotStartDateTime: `${getYMDStringFromDateObject(selectedDate)} ${selectedTimeSlot.split(' - ')[0]}:00`,
                slotEndDateTime: `${getYMDStringFromDateObject(selectedDate)} ${selectedTimeSlot.split(' - ')[1]}:00`
            };

            bookingReservation = await CarbucksService.updateReservation(appConfig, updateParams);
        } else {
            const createParams: CreateReservationParams = {
                licencePlate: formValues.licensePlateOther
                    ? formValues.licensePlateOther
                    : formValues.registrationNumber?.registrationNumber,
                carbucksGUID: selectedWorkshop?.carbucksGUID || '',
                slotStartDateTime: `${getYMDStringFromDateObject(selectedDate)} ${selectedTimeSlot.split(' - ')[0]}:00`,
                slotEndDateTime: `${getYMDStringFromDateObject(selectedDate)} ${selectedTimeSlot.split(' - ')[1]}:00`
            };

            bookingReservation = await CarbucksService.createReservation(appConfig, createParams);
        }

        if (bookingReservation === null) {
            formConfig && trackInfo(formConfig, [{ key: 'carbucks-appointment-reserved-error', value: true }]);
            setIsReservationError(true);
        } else if (bookingReservation === true && existingReservation) {
            // true is returned from updateReservation when the API returns 204 No Content (which means the update was successful)
            resetBookingState();
            onBookingReservation(existingReservation, selectedWorkshop);
        } else {
            // Track appointment booked
            formConfig &&
                typeof bookingReservation !== 'boolean' &&
                trackInfo(formConfig, [
                    { key: 'carbucks-appointment-reserved', value: true },
                    { key: 'carbucks-appointment-reserved-id', value: bookingReservation.reservationGUID },
                    { key: 'carbucks-branch', value: selectedWorkshop?.branchId || '' }
                ]);

            resetBookingState();
            typeof bookingReservation !== 'boolean' && onBookingReservation(bookingReservation, selectedWorkshop);
        }
        setIsCreateReservationLoading(false);
    };

    return (
        <ModalOverlay isOpen={isModalOpen} onDismiss={() => {}} id="carbucks-modal">
            <ModalDialog
                closeIconHandler={() => {
                    resetBookingState();
                    onCloseClick(isReservationError ? 'reservationApiFailed' : 'ok');
                }}
            >
                <ModalHeader></ModalHeader>

                {showReservationConfirm ? (
                    <>
                        <ModalBody>
                            <h4>Du er næsten færdig</h4>

                            <p>Værkstedet modtager først din booking, når du indsender din anmeldelse.</p>
                            <Box theme="secondary">
                                <p>Her er den tid, du er i gang med at booke:</p>
                                <strong>Dato og tid</strong>
                                <br />
                                {CarbucksService.prettyFormatReservationDateTime(
                                    getYMDStringFromDateObject(selectedDate)
                                ) + ` ${selectedTimeSlot}`}
                                <br />
                                <br />
                                <strong>Værksted</strong>
                                <br />
                                {selectedWorkshop?.name}
                                <br />
                                Adresse:{' '}
                                <LinkGoogleMaps
                                    streetName={selectedWorkshop?.address.streetName}
                                    streetNumber={selectedWorkshop?.address.streetNumber}
                                    postalCode={selectedWorkshop?.address.postalCode}
                                    city={selectedWorkshop?.address.city}
                                />
                                <br />
                                Telefon: <LinkPhonenumber phoneNumber={selectedWorkshop?.phoneNumber} />
                                <br />
                            </Box>

                            {isReservationError && (
                                <Message theme="error">
                                    <p>
                                        Det er desværre ikke muligt at booke en tid lige nu. Prøv igen eller fortsæt
                                        anmeldelsen uden at booke en tid.
                                    </p>
                                </Message>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <div style={{ textAlign: 'left' }}>
                                <Button
                                    theme="primary"
                                    disabled={isCreateReservationLoading}
                                    onClick={() => {
                                        onConfirmReservation();
                                    }}
                                >
                                    Fortsæt din anmeldelse
                                </Button>
                                <Button
                                    className="flos-button flos-button-widest flos-button--no-fill"
                                    disabled={isCreateReservationLoading}
                                    onClick={() => {
                                        setShowReservationConfirm(false);
                                    }}
                                >
                                    Ret booking
                                </Button>
                            </div>
                            {isCreateReservationLoading && (
                                <div>
                                    <LoadingDots />
                                </div>
                            )}
                        </ModalFooter>
                    </>
                ) : (
                    <>
                        <ModalBody>
                            <h4>Book tid hos Carbucks ({selectedWorkshop?.address.city})</h4>

                            <p>
                                Carbucks bekræfter din tid på e-mail og SMS, når du har booket. Her fortæller de dig
                                også mere om din aftale.
                            </p>

                            <DateField
                                key={resetKey}
                                dateFormat="dd-MM-yyyy"
                                selected={selectedDate}
                                onChange={onDateChange}
                                onBlur={() => {
                                    //doValidation();
                                }}
                                onFocus={(e: any) => e.target.blur()} // Prevent keyboard on mobile - https://stackoverflow.com/a/69217777
                                label="Næste ledige tid"
                                required={true}
                                aria-label="Næste ledige tid"
                                minDate={minCarbucksDate}
                                maxDate={maxCarbucksDate}
                                excludeDates={excludeDays}
                            />

                            {isTimeslotsLoading ? (
                                <div>
                                    <LoadingDots centered={false} />
                                </div>
                            ) : (
                                <div>
                                    {availableTimeslotsOnDate.length > 0 ? (
                                        <SelectableBoxField
                                            options={availableTimeslotsOnDate}
                                            value={selectedTimeSlot}
                                            onChangeValue={(v: string) => {
                                                setSelectedTimeSlot(v);
                                            }}
                                        ></SelectableBoxField>
                                    ) : (
                                        <Message>
                                            <p>Der er ingen ledige tider på den valgte dato. Prøv en anden dato.</p>
                                        </Message>
                                    )}
                                </div>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <div style={{ textAlign: 'left' }}>
                                <Button
                                    theme="primary"
                                    disabled={!selectedTimeSlot}
                                    onClick={() => {
                                        setShowReservationConfirm(true);
                                        setIsReservationError(false);
                                    }}
                                >
                                    Vælg tidspunkt
                                </Button>
                                <Button
                                    className="flos-button flos-button-widest flos-button--no-fill"
                                    onClick={() => {
                                        onCloseClick(isReservationError ? 'reservationApiFailed' : 'ok');
                                    }}
                                >
                                    Tilbage
                                </Button>
                            </div>
                        </ModalFooter>
                    </>
                )}
            </ModalDialog>
        </ModalOverlay>
    );
};

export default CarbucksBooking;
